import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchIntegrationsData, connectApp } from "./api";
import { IIntegrationApp, IntegrationsListRes } from "./types";

// Keys
export const integrationKeys = {
    root: ["integrations"] as const,
    list: () => [...integrationKeys.root, "list"] as const,
};

export function useIntegrations() {
    return useQuery<IntegrationsListRes>({
        queryKey: integrationKeys.list(),
        queryFn: fetchIntegrationsData,
        staleTime: 60_000,
    });
}

export function useConnectApp() {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: (name: string) => connectApp(name),

        onMutate: async (name: string) => {
            await qc.cancelQueries({ queryKey: integrationKeys.list() });
            const prev = qc.getQueryData<IntegrationsListRes>(integrationKeys.list());

            // optimistic: show "pending"
            if (prev) {
                const next = prev.map((app) =>
                    app.name === name
                        ? { ...app, connectionStatus: "pending" as IIntegrationApp["connectionStatus"] }
                        : app
                );
                qc.setQueryData(integrationKeys.list(), next);
            }

            return { prev };
        },

        onError: (_err, _name, ctx) => {
            if (ctx?.prev) qc.setQueryData(integrationKeys.list(), ctx.prev);
        },

        onSuccess: (res, name) => {
            // If backend returns { url }, redirect to OAuth flow
            if ("url" in res && res.url) {
                window.location.href = res.url;
                return;
            }

            // Otherwise mark as connected and refresh from server
            qc.setQueryData<IntegrationsListRes>(integrationKeys.list(), (curr) => {
                if (!curr) return curr as any;
                return curr.map((a) =>
                    a.name === name ? { ...a, connectionStatus: "connected" } : a
                );
            });

            qc.invalidateQueries({ queryKey: integrationKeys.list() });
        },
    });
}
